/**
 * Cola offline, consecutivos y helpers de error para pedidos POSKN.
 */

import {
	COLA_MAX_ITEMS,
	COLA_STORAGE_KEY,
	COLA_STORAGE_BAK_KEY,
	initQueueStorage,
	getQueueSnapshot,
	isQueueInitialized,
	persistQueueStorage,
	setQueueCacheMemory,
	normalizeQueueItem,
	backupQueueToLocalStorage,
} from './orders-queue-db.js';

const COLA_REINTENTOS_POR_ITEM = 6;
const COLA_DELAY_REINTENTO_MS = 2000;
const COLA_SYNC_INTERVAL_MS = 10000;
const COLA_SYNC_STUCK_MS = 8 * 60 * 1000;
const COLA_SENDING_LEASE_MS = 120 * 1000;
const COLA_ANULADOS_KEY = 'poskn_cola_anulados';

function queueItemCid(item) {
	if (!item) return '';
	return item.client_pedido_id || (item.pedidoCompleto && item.pedidoCompleto.client_pedido_id) || '';
}

export function posknRespuestaEsAckPedido(data) {
	if (!data || typeof data !== 'object') return false;
	const hasId = data.order_id != null || data.order_number != null;
	if (!hasId) return false;
	if (data.ack === true) return true;
	if (data.encontrado && data.order_id) return true;
	return false;
}

function colaItemSendingReciente(item) {
	if (!item || item.outbox_status !== 'sending') return false;
	const at = Date.parse(item.sendingAt || 0) || 0;
	if (!at) return false;
	return Date.now() - at < COLA_SENDING_LEASE_MS;
}

function leerAnuladosDeStore(store) {
	try {
		const raw = store.getItem(COLA_ANULADOS_KEY);
		const arr = raw ? JSON.parse(raw) : [];
		return Array.isArray(arr) ? arr.filter(Boolean) : [];
	} catch (e) {
		return [];
	}
}

function leerAnuladosCola(win) {
	const ls = leerAnuladosDeStore(win.localStorage);
	const ss = leerAnuladosDeStore(win.sessionStorage);
	return Array.from(new Set(ls.concat(ss)));
}

function persistirAnuladosCola(win, list) {
	const payload = JSON.stringify((list || []).slice(-200));
	try {
		win.localStorage.setItem(COLA_ANULADOS_KEY, payload);
	} catch (e) {
		/* ignore */
	}
	try {
		win.sessionStorage.setItem(COLA_ANULADOS_KEY, payload);
	} catch (e2) {
		/* ignore */
	}
}

function marcarAnuladoCola(win, clientPedidoId) {
	if (!clientPedidoId) return;
	const list = leerAnuladosCola(win);
	if (list.indexOf(clientPedidoId) === -1) list.push(clientPedidoId);
	persistirAnuladosCola(win, list);
}

function estaAnuladoCola(win, clientPedidoId) {
	if (!clientPedidoId) return false;
	return leerAnuladosCola(win).indexOf(clientPedidoId) !== -1;
}

function limpiarFlagPedidoEnviando(win, clientPedidoId) {
	const state = os(win);
	if (!clientPedidoId || state.clientPedidoIdEnviandoActivo === clientPedidoId) {
		state.clientPedidoIdEnviandoActivo = null;
	}
	try {
		const raw = win.localStorage.getItem('pos_pedido_enviando');
		if (!raw) return;
		if (!clientPedidoId) {
			win.localStorage.removeItem('pos_pedido_enviando');
			return;
		}
		const env = JSON.parse(raw);
		const envCid = env.client_pedido_id || (env.pedidoCompleto && env.pedidoCompleto.client_pedido_id) || '';
		if (!envCid || envCid === clientPedidoId) {
			win.localStorage.removeItem('pos_pedido_enviando');
		}
	} catch (e) {
		try {
			win.localStorage.removeItem('pos_pedido_enviando');
		} catch (e2) {
			/* ignore */
		}
	}
}

function bumpColaSyncGeneration(win) {
	const state = os(win);
	state.colaSyncGeneration = (state.colaSyncGeneration || 0) + 1;
	return state.colaSyncGeneration;
}

function esErrorRed(xhr, status) {
	return !xhr || status === 'timeout' || status === 'error' || (xhr && xhr.status === 0);
}

function cajaAbiertaLocalmente($, win) {
	const cache = win._posknCajaCache;
	if (cache && cache.estado === 'abierta') return true;
	if (cache && (cache.estado === 'cerrada' || cache.estado === 'desconocido')) return false;
	return $('#estado-caja-texto').hasClass('estado-abierta');
}

function notificarPedidoEnColaOffline(win, numeroPedido) {
	const n = typeof win.getColaPendientesSubir === 'function' ? win.getColaPendientesSubir().length : 0;
	const num = numeroPedido || 'local';
	if (typeof win.mostrarNotificacion === 'function') {
		win.mostrarNotificacion(
			`Sin conexión: pedido ${num} guardado en cola (${n} pendiente(s)). Se subirá automáticamente al reconectar.`,
			'warning',
		);
	}
	if (typeof win.actualizarIndicadorPendientesSubir === 'function') {
		win.actualizarIndicadorPendientesSubir();
	}
}

function notifyTrim(win, msg) {
	if (typeof win.mostrarNotificacion === 'function') {
		win.mostrarNotificacion(msg, 'warning');
	}
}

function persistQueue(win, arr) {
	const list = Array.isArray(arr) ? arr.slice() : [];
	setQueueCacheMemory(list);
	const snap = getQueueSnapshot();
	const ls = backupQueueToLocalStorage(win, snap, (msg) => notifyTrim(win, msg));
	persistQueueStorage(win, snap, (msg) => notifyTrim(win, msg)).then((result) => {
		if (!result.ok && typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('No se pudo guardar la cola offline. Revisa el almacenamiento del navegador.', 'error');
		}
	});
	return ls.ok !== false;
}

function initOfflineQueueBoot(win, $) {
	if (win._posknOfflineBootBound) return;
	win._posknOfflineBootBound = true;

	$(() => {
		initQueueStorage(win).then(() => {
			try {
				const rawEnviando = win.localStorage.getItem('pos_pedido_enviando');
				if (rawEnviando) {
					const env = JSON.parse(rawEnviando);
					if (env.pedidoCompleto && env.numeroPedido) {
						const cid =
							env.client_pedido_id ||
							(env.pedidoCompleto && env.pedidoCompleto.client_pedido_id);
						if (cid && estaAnuladoCola(win, cid)) {
							try {
								win.localStorage.removeItem('pos_pedido_enviando');
							} catch (eAnul) {
								/* ignore */
							}
						} else {
							let yaEnCola =
								typeof win.yaEstaEnColaPendientes === 'function' &&
								win.yaEstaEnColaPendientes(cid);
							if (
								!yaEnCola &&
								typeof win.guardarEnColaPendienteSubir === 'function'
							) {
								win.guardarEnColaPendienteSubir(
									env.pedidoCompleto,
									env.numeroPedido,
									env.totalUsd || 0,
									env.totalBs || 0,
									env.client_pedido_id,
								);
							}
							yaEnCola =
								typeof win.yaEstaEnColaPendientes === 'function' &&
								win.yaEstaEnColaPendientes(cid);
							const numMatch = String(env.numeroPedido).match(/#?0*(\d+)/);
							if (numMatch) {
								const state = os(win);
								state.contadorPedidos = Math.max(
									state.contadorPedidos || 1,
									parseInt(numMatch[1], 10) + 1,
								);
								try {
									win.localStorage.setItem(
										'contador_pedidos',
										state.contadorPedidos.toString(),
									);
								} catch (e) {
									/* ignore */
								}
							}
							if (yaEnCola) {
								try {
									win.localStorage.removeItem('pos_pedido_enviando');
								} catch (e2) {
									/* ignore */
								}
								if (typeof win.borrarBorradorPedido === 'function') {
									win.borrarBorradorPedido();
								}
								win.actualizarIndicadorPendientesSubir();
								if (typeof win.mostrarNotificacion === 'function') {
									win.mostrarNotificacion(
										'Se recuperó un pedido que estaba enviando. Está en pendientes de subir.',
										'info',
									);
								}
							} else if (typeof win.mostrarNotificacion === 'function') {
								win.mostrarNotificacion(
									'No se pudo guardar el pedido en pendientes. Mantén esta pestaña abierta y recarga para reintentar.',
									'warning',
								);
							}
						}
					} else {
						try {
							win.localStorage.removeItem('pos_pedido_enviando');
						} catch (e2) {
							/* ignore */
						}
					}
				}
			} catch (e) {
				/* No borrar pos_pedido_enviando si algo falla */
			}

			win.actualizarIndicadorPendientesSubir();
			if (win.getColaPendientesSubir().length > 0) {
				if (typeof win.posknRegisterQueueBackgroundSync === 'function') {
					win.posknRegisterQueueBackgroundSync();
				}
			}
			win.procesarColaSubida();
			win.setTimeout(() => {
				if (win.getColaPendientesSubir().length > 0) win.procesarColaSubida();
			}, 2500);
			win.setTimeout(() => {
				if (win.getColaPendientesSubir().length > 0) win.procesarColaSubida();
			}, 8000);
			win.setInterval(() => {
				if (win.getColaPendientesSubir().length > 0) win.procesarColaSubida();
			}, COLA_SYNC_INTERVAL_MS);
			win.addEventListener('focus', () => {
				if (win.getColaPendientesSubir().length > 0) win.procesarColaSubida();
			});
			win.addEventListener('pageshow', () => {
				if (win.getColaPendientesSubir().length > 0) win.procesarColaSubida();
			});
		});

		win.addEventListener('online', () => {
			if (typeof win.procesarColaSubida !== 'function') return;
			win.procesarColaSubida();
			if (
				typeof win.getColaPendientesSubir === 'function' &&
				win.getColaPendientesSubir().length > 0 &&
				typeof win.mostrarNotificacion === 'function'
			) {
				win.mostrarNotificacion(
					'Conexión restablecida. Subiendo pedidos pendientes...',
					'info',
				);
			}
		});
	});
}

export function initOrderState(win) {
	win.posknOrderState = win.posknOrderState || {
		contadorPedidos: parseInt(win.localStorage.getItem('contador_pedidos') || '1', 10),
		consecutivoSincronizado: false,
		clientPedidoIdEnviandoActivo: null,
		procesandoColaSubida: false,
	};
	return win.posknOrderState;
}

function os(win) {
	return win.posknOrderState || initOrderState(win);
}

function maxConsecutivoDesdeColaPendiente(win) {
	let max = 0;
	try {
		win.getColaPendientesSubir().forEach((item) => {
			const num = item.numeroPedido || (item.pedidoCompleto && item.pedidoCompleto.numero);
			const m = String(num || '').match(/#?0*(\d+)/);
			if (m) max = Math.max(max, parseInt(m[1], 10) + 1);
		});
	} catch (e) {}
	return max;
}

function aplicarConsecutivoLocal(win, serverNext) {
	const state = os(win);
	const desdeCola = maxConsecutivoDesdeColaPendiente(win);
	state.contadorPedidos = Math.max(state.contadorPedidos || 1, parseInt(serverNext, 10) || 1, desdeCola, 1);
	try {
		win.localStorage.setItem('contador_pedidos', state.contadorPedidos.toString());
	} catch (e) {}
	state.consecutivoSincronizado = true;
}

function newClientPedidoId(win, contadorHint) {
	const hint = contadorHint != null ? String(contadorHint) : '';
	try {
		if (win.crypto && typeof win.crypto.randomUUID === 'function') {
			return `pos-${win.crypto.randomUUID()}${hint ? '-' + hint : ''}`;
		}
	} catch (e) {
		/* ignore */
	}
	return `pos-${Date.now()}-${hint || '0'}-${Math.random().toString(36).slice(2, 11)}`;
}

function fallbackReservaLocalConsecutivo(win, callback) {
	const state = os(win);
	const numeroPedido = `#${state.contadorPedidos.toString().padStart(5, '0')}`;
	state.contadorPedidos++;
	try {
		win.localStorage.setItem('contador_pedidos', state.contadorPedidos.toString());
	} catch (e) {}
	const clientPedidoId = newClientPedidoId(win, state.contadorPedidos - 1);
	callback(numeroPedido, clientPedidoId);
}

export function attachOrdersQueueGlobals(win, $) {
	initOrderState(win);
	initQueueStorage(win);

	win.posknInitQueueStorage = () => initQueueStorage(win);

	win.maxConsecutivoDesdeColaPendiente = () => maxConsecutivoDesdeColaPendiente(win);

	win.aplicarConsecutivoLocal = (serverNext) => aplicarConsecutivoLocal(win, serverNext);

	win.sincronizarConsecutivoConServidor = (callback) => {
		win.POSKN.api.peekConsecutive(8000)
			.then((res) => {
				if (res && res.success && res.data && res.data.next != null) {
					aplicarConsecutivoLocal(win, res.data.next);
				} else {
					os(win).consecutivoSincronizado = true;
				}
				if (typeof callback === 'function') callback(true);
			})
			.catch(() => {
				os(win).consecutivoSincronizado = true;
				if (typeof callback === 'function') callback(false);
			});
	};

	win.fallbackReservaLocalConsecutivo = (callback) => fallbackReservaLocalConsecutivo(win, callback);

	win.reservarNumeroPedidoServidor = (callback) => {
		win.POSKN.api.reserveConsecutive(10000)
			.then((res) => {
				if (res && res.success && res.data && res.data.numero != null) {
					const state = os(win);
					const num = parseInt(res.data.numero, 10);
					const numeroPedido = res.data.formatted || `#${num.toString().padStart(5, '0')}`;
					state.contadorPedidos = num + 1;
					try {
						win.localStorage.setItem('contador_pedidos', state.contadorPedidos.toString());
					} catch (e) {}
					const clientPedidoId = newClientPedidoId(win, num);
					callback(numeroPedido, clientPedidoId);
					return;
				}
				fallbackReservaLocalConsecutivo(win, callback);
			})
			.catch(() => fallbackReservaLocalConsecutivo(win, callback));
	};

	win.pedidoEstaEnviandoseAhora = (clientId) => {
		if (!clientId) return false;
		const state = os(win);
		if (state.clientPedidoIdEnviandoActivo && state.clientPedidoIdEnviandoActivo === clientId) {
			return !!win.procesandoPedido;
		}
		if (!win.procesandoPedido) return false;
		try {
			const raw = win.localStorage.getItem('pos_pedido_enviando');
			if (!raw) return false;
			const env = JSON.parse(raw);
			const envCid = env.client_pedido_id || (env.pedidoCompleto && env.pedidoCompleto.client_pedido_id) || '';
			return envCid === clientId;
		} catch (e) {
			return false;
		}
	};

	win.posknHayPedidosPendientesLocales = () => {
		let count = 0;
		let reason = '';
		try {
			if (typeof win.getColaPendientesSubir === 'function') {
				count = win.getColaPendientesSubir().length;
				if (count > 0) reason = 'cola';
			}
		} catch (e) {
			/* ignore */
		}
		if (count === 0) {
			try {
				if (win.localStorage.getItem('pos_pedido_enviando')) {
					count = 1;
					reason = 'enviando';
				}
			} catch (e2) {
				/* ignore */
			}
		}
		if (count === 0 && win.procesandoPedido) {
			count = 1;
			reason = 'procesando';
		}
		return { ok: count > 0, count, reason };
	};

	win.getColaPendientesSubir = () => {
		// Tras init, confiar siempre en la caché (también si está vacía).
		let list;
		if (isQueueInitialized()) {
			list = getQueueSnapshot();
		} else {
			try {
				const q = JSON.parse(win.localStorage.getItem(COLA_STORAGE_KEY) || '[]');
				if (!Array.isArray(q)) list = [];
				else list = q.map(normalizeQueueItem).filter(Boolean);
			} catch (e) {
				list = [];
			}
		}
		return list.filter((item) => !estaAnuladoCola(win, queueItemCid(item)));
	};

	win.setColaPendientesSubir = (arr) => {
		const ok = persistQueue(win, arr);
		if (ok && typeof win.actualizarIndicadorPendientesSubir === 'function') {
			win.actualizarIndicadorPendientesSubir();
		}
		return ok;
	};

	win.quitarDeColaPendientesSubir = (clientPedidoId) => {
		if (!clientPedidoId) return;
		const before = win.getColaPendientesSubir();
		const cola = before.filter((item) => queueItemCid(item) !== clientPedidoId);
		if (cola.length < before.length) win.setColaPendientesSubir(cola);
	};

	win.posknRespuestaEsAckPedido = posknRespuestaEsAckPedido;

	win.patchColaPendiente = (clientPedidoId, patch) => {
		if (!clientPedidoId || !patch || typeof patch !== 'object') return false;
		const cola = win.getColaPendientesSubir();
		let found = false;
		const next = cola.map((item) => {
			if (queueItemCid(item) !== clientPedidoId) return item;
			found = true;
			return { ...item, ...patch };
		});
		if (!found) return false;
		return win.setColaPendientesSubir(next);
	};

	win.marcarColaOutboxEstado = (clientPedidoId, status, extra) => {
		const patch = { outbox_status: status || 'queued', ...(extra || {}) };
		if (status === 'sending') {
			patch.sendingAt = new Date().toISOString();
		}
		if (status === 'queued' || status === 'failed_business') {
			patch.sendingAt = '';
		}
		return win.patchColaPendiente(clientPedidoId, patch);
	};

	win.posknParseMensajeErrorAjax = (xhrOrResponse) => {
		try {
			let j = xhrOrResponse && xhrOrResponse.responseJSON;
			if (!j && xhrOrResponse && xhrOrResponse.responseText) {
				j = JSON.parse(xhrOrResponse.responseText);
			}
			if (!j && xhrOrResponse && xhrOrResponse.data !== undefined) {
				j = xhrOrResponse;
			}
			if (!j) return '';
			// REST WP_Error: { code, message, data: { status } }
			if (typeof j.message === 'string' && j.message && (j.code || j.data)) {
				return j.message;
			}
			if (j.data != null) {
				if (typeof j.data === 'string') return j.data;
				if (typeof j.data === 'object' && j.data.message) return String(j.data.message);
			}
			if (typeof j === 'string') return j;
		} catch (e) {}
		return '';
	};

	win.posknErrorPedidoEsRecuperable = (msg, xhrOrResponse) => {
		if (!msg) return true;
		const noRecuperables = [
			'receta tiene productos eliminados',
			'Stock insuficiente',
			'producto inválido',
			'eliminado en el carrito',
			'no es válida o está inactiva',
			'Acción no válida',
			'no autenticado',
			'Pedido no encontrado para actualizar',
			'No se pueden eliminar pedidos',
			'cantidades deben ser mayores',
		];
		for (let i = 0; i < noRecuperables.length; i++) {
			if (msg.toLowerCase().indexOf(noRecuperables[i].toLowerCase()) !== -1) return false;
		}
		try {
			if (xhrOrResponse && xhrOrResponse.responseJSON && xhrOrResponse.responseJSON.retry === true) return true;
			if (xhrOrResponse && xhrOrResponse.retry === true) return true;
		} catch (e) {}
		return true;
	};

	win.posknAbortarEnvioPedidoPorError = (msg, clientPedidoId) => {
		const state = os(win);
		state.envioPedidoAbortGen = (state.envioPedidoAbortGen || 0) + 1;
		state.clientPedidoIdEnviandoActivo = null;
		try {
			win.localStorage.removeItem('pos_pedido_enviando');
			if (clientPedidoId && typeof win.marcarColaOutboxEstado === 'function') {
				win.marcarColaOutboxEstado(clientPedidoId, 'failed_business', {
					_error_servidor: msg || 'No se pudo crear el pedido.',
				});
			}
		} catch (e) {}
		if (typeof win.procesandoPedido !== 'undefined') {
			win.procesandoPedido = false;
		}
		$('#btn-crear-pedido').prop('disabled', false).text('CREAR PEDIDO');
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion(msg || 'No se pudo crear el pedido.', 'error');
		}
	};

	win.anularPedidoPendienteSubir = (clientPedidoId) => {
		if (!clientPedidoId) return;
		const confirmFn = typeof win.confirm === 'function' ? win.confirm.bind(win) : null;
		if (confirmFn && !confirmFn('¿Anular este envío? El pedido se quitará de pendientes y no se subirá al servidor.')) {
			return;
		}
		marcarAnuladoCola(win, clientPedidoId);
		bumpColaSyncGeneration(win);
		win._posknColaPausadaHasta = Date.now() + 15000;
		if (typeof win.posknAbortarEnvioPedidoActivo === 'function') {
			win.posknAbortarEnvioPedidoActivo(null);
		}
		limpiarFlagPedidoEnviando(win, null);
		// Forzar vaciado del ítem en memoria + IDB (serializado; no lo pisa un write viejo).
		const rest = getQueueSnapshot().filter((item) => queueItemCid(item) !== clientPedidoId);
		win.setColaPendientesSubir(rest);
		try {
			win.localStorage.removeItem('pos_pedido_enviando');
			win.localStorage.removeItem('pos_borrador_pedido');
			win.localStorage.removeItem('pos_borrador_pedido_bak');
		} catch (e) {
			/* ignore */
		}
		if (typeof win.procesandoPedido !== 'undefined') {
			win.procesandoPedido = false;
		}
		$('#btn-crear-pedido').prop('disabled', false).text('CREAR PEDIDO');
		if (typeof win.actualizarIndicadorPendientesSubir === 'function') {
			win.actualizarIndicadorPendientesSubir();
		}
		if (typeof win.cerrarModalListaPendientes === 'function') win.cerrarModalListaPendientes();
		if (typeof win.verListaPendientesSubir === 'function') win.verListaPendientesSubir();
		if (win.getColaPendientesSubir().length > 0) {
			const $modal = $('#modal-lista-pendientes-subir');
			if ($modal.length) $modal.css('display', 'flex').show();
		}
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('Envío anulado. El pedido ya no se subirá.', 'info');
		}
	};

	win.vaciarColaPendientesSubir = () => {
		const confirmFn = typeof win.confirm === 'function' ? win.confirm.bind(win) : null;
		if (confirmFn && !confirmFn('¿Vaciar toda la cola offline? Ningún pendiente se subirá al servidor.')) {
			return;
		}
		const cola = getQueueSnapshot();
		cola.forEach((item) => {
			const cid = queueItemCid(item);
			if (cid) marcarAnuladoCola(win, cid);
		});
		bumpColaSyncGeneration(win);
		win._posknColaPausadaHasta = Date.now() + 30000;
		if (typeof win.posknAbortarEnvioPedidoActivo === 'function') {
			win.posknAbortarEnvioPedidoActivo(null);
		}
		limpiarFlagPedidoEnviando(win, null);
		win.setColaPendientesSubir([]);
		try {
			win.localStorage.removeItem('pos_pedido_enviando');
			win.localStorage.removeItem('pos_borrador_pedido');
			win.localStorage.removeItem('pos_borrador_pedido_bak');
			win.localStorage.removeItem(COLA_STORAGE_KEY);
			win.localStorage.removeItem(COLA_STORAGE_BAK_KEY);
		} catch (e) {
			/* ignore */
		}
		if (typeof win.procesandoPedido !== 'undefined') {
			win.procesandoPedido = false;
		}
		$('#btn-crear-pedido').prop('disabled', false).text('CREAR PEDIDO');
		if (typeof win.cerrarModalListaPendientes === 'function') win.cerrarModalListaPendientes();
		if (typeof win.actualizarIndicadorPendientesSubir === 'function') {
			win.actualizarIndicadorPendientesSubir();
		}
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('Cola offline vaciada.', 'info');
		}
	};

	win.yaEstaEnColaPendientes = (clientPedidoId) => {
		if (!clientPedidoId) return false;
		return win.getColaPendientesSubir().some((item) => {
			const cid = item.client_pedido_id || (item.pedidoCompleto && item.pedidoCompleto.client_pedido_id);
			return cid === clientPedidoId;
		});
	};

	win.guardarEnColaPendienteSubir = (pedidoCompleto, numeroPedido, totalUsd, totalBs, clientPedidoId) => {
		const cid = clientPedidoId || (pedidoCompleto && pedidoCompleto.client_pedido_id) || '';
		if (cid && estaAnuladoCola(win, cid)) return false;
		if (win._posknColaPausadaHasta && Date.now() < win._posknColaPausadaHasta && cid && estaAnuladoCola(win, cid)) {
			return false;
		}
		if (cid && win.yaEstaEnColaPendientes(cid)) return true;
		const cola = win.getColaPendientesSubir();
		if (cola.length >= COLA_MAX_ITEMS) {
			notifyTrim(
				win,
				`Cola offline llena (${COLA_MAX_ITEMS}). No se descartó ninguna venta anterior. Sube o reconecta antes de cobrar otra.`,
			);
			return false;
		}
		const cajaId =
			(win._posknCajaCache && win._posknCajaCache.data && win._posknCajaCache.data.caja_id) || 0;
		if (pedidoCompleto && typeof pedidoCompleto === 'object' && cajaId && !pedidoCompleto.caja_id_origen) {
			pedidoCompleto.caja_id_origen = cajaId;
		}
		cola.push({
			pedidoCompleto,
			numeroPedido,
			totalUsd,
			totalBs,
			client_pedido_id: clientPedidoId || (pedidoCompleto && pedidoCompleto.client_pedido_id) || '',
			caja_id: cajaId || 0,
			fechaGuardado: new Date().toISOString(),
			intentos: 0,
			outbox_status: 'queued',
			sendingAt: '',
		});
		const ok = win.setColaPendientesSubir(cola);
		if (ok) {
			if (typeof win.posknRegisterQueueBackgroundSync === 'function') {
				win.posknRegisterQueueBackgroundSync();
			}
			if (typeof win.posknNotifySwQueuePending === 'function') {
				win.posknNotifySwQueuePending();
			}
		}
		return ok;
	};

	win.limpiarEstadoDespuesDeGuardarEnCola = () => {
		try {
			win.localStorage.removeItem('pos_borrador_pedido');
			win.localStorage.removeItem('pos_borrador_pedido_bak');
			win.localStorage.removeItem('pos_pedido_enviando');
		} catch (e) {}
		if (typeof win.borrarBorradorPedido === 'function') {
			win.borrarBorradorPedido();
		}
		if (win.carrito && Array.isArray(win.carrito)) {
			win.carrito.length = 0;
		} else {
			win.carrito = [];
		}
		// El payload en cola ya trae pedido_existente. Limpiar el vínculo vivo
		// evita que el siguiente cobro se monte en el mismo WC.
		win.pedidoMesaAbierto = null;
		try {
			win.sessionStorage.removeItem('poskn_knia_abierto');
		} catch (eKniaCola) {
			/* ignore */
		}
		win.mesaSeleccionada = null;
		$('#mesa-badge').hide();
		$('#mesa-seleccionada-compacto').hide();
		$('#mesa-indicador-carrito').hide();
		win.clienteSeleccionado = null;
		$('#cliente-seleccionado').hide();
		$('#cliente-badge').hide();
		$('#cliente-seleccionado-compacto').hide();
		$('#cliente-buscar').val('');
		if (typeof win.descuentoActual !== 'undefined') {
			win.descuentoActual = { tipo: 'porcentaje', valor: 0, activo: false };
		}
		$('#descuento-tipo').val('porcentaje');
		$('#descuento-valor').val('');
		$('#descuento-activo').hide();
		if (typeof win.cerrarModal === 'function') win.cerrarModal('modal-pago');
		if (typeof win.actualizarCarrito === 'function') win.actualizarCarrito();
		if (typeof win.seleccionarTipoServicio === 'function') win.seleccionarTipoServicio('comer');
		if (typeof win.actualizarBotonesMesa === 'function') win.actualizarBotonesMesa();
	};

	win.actualizarIndicadorPendientesSubir = () => {
		const n = win.getColaPendientesSubir().length;
		const $bar = $('#barra-pendientes-subir');
		if ($bar.length) {
			$bar.hide();
		}
		const $countTab = $('#pedidos-sin-conexion-count');
		if ($countTab.length) $countTab.text(n);
		if (typeof win.posknUpdateConnectionStatus === 'function') {
			win.posknUpdateConnectionStatus();
		}
	};

	win.verListaPendientesSubir = () => {
		const cola = win.getColaPendientesSubir();
		const $modal = $('#modal-lista-pendientes-subir');
		const $body = $('#lista-pendientes-subir-body');
		if (!cola.length) {
			$body.html('<p>No hay pedidos pendientes.</p>');
		} else {
			let html = '<ul style="list-style: none; padding: 0; margin: 0;">';
			cola.forEach((item, i) => {
				const pc = item.pedidoCompleto || {};
				const num = item.numeroPedido || pc.numero || `#${i + 1}`;
				const totalUsd = item.totalUsd != null ? item.totalUsd : pc.total && pc.total.usd;
				const totalBs = item.totalBs != null ? item.totalBs : pc.total && pc.total.bs;
				let fecha = item.fechaGuardado || pc.fecha || '';
				if (fecha) {
					try {
						fecha = new Date(fecha).toLocaleString('es');
					} catch (e) {}
				}
				const cid = item.client_pedido_id || (pc && pc.client_pedido_id) || '';
				const idCorto = cid.toString().slice(-8);
				const errMsg = item._error_servidor ? String(item._error_servidor) : '';
				const intentos = parseInt(item.intentos, 10) || 0;
				let ultimo = item.ultimoIntento || '';
				if (ultimo) {
					try {
						ultimo = new Date(ultimo).toLocaleTimeString('es');
					} catch (e2) {}
				}
				html += `<li style="border: 1px solid #eee; padding: 10px; margin-bottom: 8px; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">`;
				html += `<div><strong>${num || `Pedido ${i + 1}`}</strong>`;
				html += ` &nbsp;|&nbsp; $${(parseFloat(totalUsd) || 0).toFixed(2)} USD`;
				if (totalBs) html += ` / Bs. ${(parseFloat(totalBs) || 0).toFixed(2)}`;
				html += `<br><span style="font-size: 12px; color: #666;">Guardado: ${fecha || '—'}${idCorto ? ` &nbsp;(id: …${idCorto})` : ''}${intentos ? ` · intentos: ${intentos}` : ''}${ultimo ? ` · último: ${ultimo}` : ''}</span>`;
				if (errMsg) {
					html += `<br><span style="font-size: 12px; color: #b91c1c;">Error: ${errMsg.replace(/</g, '&lt;')}</span>`;
				}
				html += `</div><div style="display:flex;gap:6px;flex-shrink:0;">`;
				html += `<button type="button" class="button" style="background:#1565c0;color:#fff;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:12px;" onclick="procesarColaSubida();">Subir</button>`;
				html += `<button type="button" class="button" style="background: #dc3545; color: #fff; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;" onclick="anularPedidoPendienteSubir('${(cid || '').replace(/'/g, "\\'")}');">Anular envío</button>`;
				html += '</div></li>';
			});
			html += '</ul>';
			$body.html(html);
		}
		$modal.css('display', 'flex').show();
	};

	win.cerrarModalListaPendientes = () => {
		$('#modal-lista-pendientes-subir').hide();
	};

	win.procesarColaSubida = () => {
		const state = os(win);
		const now = Date.now();
		if (state.procesandoColaSubida) {
			const started = state.colaSyncStartedAt || 0;
			if (started && now - started < COLA_SYNC_STUCK_MS) return;
			state.procesandoColaSubida = false;
			win.posknQueueSyncing = false;
		}
		if (win.procesandoPedido) {
			const since = state.procesandoPedidoDesde || 0;
			if (since && now - since < 60000) return;
			win.procesandoPedido = false;
			state.clientPedidoIdEnviandoActivo = null;
		}
		if (win._posknColaPausadaHasta && Date.now() < win._posknColaPausadaHasta) return;
		const cola = win.getColaPendientesSubir().filter((item) => {
			const cid = queueItemCid(item);
			return cid && !estaAnuladoCola(win, cid);
		});
		if (cola.length === 0) {
			// Purgar de almacenamiento cualquier ítem anulado que siga en caché cruda.
			const raw = getQueueSnapshot();
			const vivos = raw.filter((item) => !estaAnuladoCola(win, queueItemCid(item)));
			if (vivos.length !== raw.length) {
				win.setColaPendientesSubir(vivos);
			}
			return;
		}
		const syncGen = state.colaSyncGeneration || 0;
		state.procesandoColaSubida = true;
		state.colaSyncStartedAt = Date.now();
		win.posknQueueSyncing = true;
		if (typeof win.posknUpdateConnectionStatus === 'function') {
			win.posknUpdateConnectionStatus();
		}
		const historialPedidos = JSON.parse(win.localStorage.getItem('historial_pedidos') || '[]');
		let procesados = 0;
		const nuevaCola = [];

		function finalizarCola() {
			try {
				if ((state.colaSyncGeneration || 0) !== syncGen) {
					// Anular/vaciar durante el ciclo: no reescribir la cola.
					return;
				}
				const vigentes = new Set(
					win.getColaPendientesSubir().map(queueItemCid).filter(Boolean),
				);
				const filtrada = nuevaCola.filter((item) => {
					const cid = queueItemCid(item);
					return cid && vigentes.has(cid) && !estaAnuladoCola(win, cid);
				});
				win.setColaPendientesSubir(filtrada);
				if (procesados > 0) {
					win.localStorage.setItem('historial_pedidos', JSON.stringify(historialPedidos));
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion(`Se subieron ${procesados} pedido(s) correctamente.`, 'success');
					}
					if (typeof win.posknRefreshPedidosUi === 'function') {
						win.posknRefreshPedidosUi(false);
					}
				}
			} finally {
				state.procesandoColaSubida = false;
				state.colaSyncStartedAt = 0;
				win.posknQueueSyncing = false;
				if (typeof win.posknUpdateConnectionStatus === 'function') {
					win.posknUpdateConnectionStatus();
				}
			}
		}

		function enviarUno(index, reintentoItem) {
			reintentoItem = reintentoItem || 0;
			if ((state.colaSyncGeneration || 0) !== syncGen) {
				state.procesandoColaSubida = false;
				win.posknQueueSyncing = false;
				if (typeof win.posknUpdateConnectionStatus === 'function') {
					win.posknUpdateConnectionStatus();
				}
				return;
			}
			if (index >= cola.length) {
				finalizarCola();
				return;
			}
			const item = cola[index];
			const pc = item.pedidoCompleto;
			const clientId = item.client_pedido_id || (pc && pc.client_pedido_id) || '';
			if (!clientId || estaAnuladoCola(win, clientId)) {
				enviarUno(index + 1, 0);
				return;
			}
			if (win.pedidoEstaEnviandoseAhora(clientId)) {
				nuevaCola.push(item);
				enviarUno(index + 1, 0);
				return;
			}
			if (item.outbox_status === 'failed_business' && reintentoItem === 0) {
				nuevaCola.push(item);
				enviarUno(index + 1, 0);
				return;
			}
			if (colaItemSendingReciente(item) && reintentoItem === 0) {
				nuevaCola.push(item);
				enviarUno(index + 1, 0);
				return;
			}
			item.outbox_status = 'sending';
			item.sendingAt = new Date().toISOString();
			if (typeof win.marcarColaOutboxEstado === 'function') {
				win.marcarColaOutboxEstado(clientId, 'sending');
			}
			// Reparar cierre Knia/mesa: close-123 → pedido_existente + cerrar_cuenta.
			if (pc && typeof pc === 'object') {
				const mClose = String(clientId).match(/^close-(\d+)$/);
				if (mClose) {
					pc.pedido_existente = parseInt(mClose[1], 10);
					pc.cerrar_cuenta = true;
					if (!pc.canal && pc.knia_code) pc.canal = 'knia';
					pc.client_pedido_id = clientId;
				}
			}
			const esCocina = !!(
				pc &&
				(pc.enviar_cocina ||
					(pc.estado === 'pending' &&
						!pc.cerrar_cuenta &&
						(!pc.metodosPago || !pc.metodosPago.length) &&
						String(clientId).indexOf('pos-enviar-') === 0) ||
					String(clientId).indexOf('cocina-') === 0)
			);
			const marcarExitoCola = (data) => {
				if (data && data.pos_numero) {
					pc.numero = data.pos_numero;
					item.numeroPedido = data.pos_numero;
				}
				pc.wc_order_id = data.order_id;
				pc.wc_order_number = data.order_number;
				win.quitarDeColaPendientesSubir(clientId);
				if (!esCocina) {
					const yaEnHistorial = historialPedidos.some(
						(p) =>
							(p.wc_order_id && p.wc_order_id == data.order_id) ||
							(p.wc_order_number && p.wc_order_number == data.order_number)
					);
					if (!yaEnHistorial) historialPedidos.push(pc);
					if (typeof win.posknRefrescarStockTrasPedido === 'function') {
						win.posknRefrescarStockTrasPedido(pc.items || []);
					}
				}
				procesados++;
				if (typeof win.cargarColaKnia === 'function') {
					win.cargarColaKnia(false);
				}
				enviarUno(index + 1, 0);
			};
			const verificarSiYaEnServidor = () => {
				const api = win.POSKN && win.POSKN.api;
				if (!api || typeof api.verifyPedido !== 'function') {
					return Promise.resolve(null);
				}
				const oidHint = parseInt(pc.wc_order_id || pc.pedido_existente || 0, 10) || 0;
				return api
					.verifyPedido(clientId, oidHint, 12000)
					.then((res) => {
						const d = res && res.data;
						if (!d || !d.encontrado || !d.order_id) return null;
						if (esCocina) return d;
						return d.facturado ? d : null;
					})
					.catch(() => null);
			};
			const req =
				esCocina && win.POSKN.api && typeof win.POSKN.api.enviarCocina === 'function'
					? win.POSKN.api.enviarCocina(pc, clientId, 60000)
					: win.POSKN.api.createOrder(pc, clientId, 60000);
			req
				.then((response) => {
					const data = response && response.data;
					const isAck = posknRespuestaEsAckPedido(data);
					if (response && response.success && isAck) {
						const stAck = String((data && data.status) || '')
							.toLowerCase()
							.replace(/^wc-/, '');
						const cobroEsperabaFactura =
							!esCocina &&
							pc &&
							(pc.cerrar_cuenta || (Array.isArray(pc.metodosPago) && pc.metodosPago.length));
						if (cobroEsperabaFactura && stAck === 'pending') {
							item.outbox_status = 'queued';
							item._error_servidor = 'El cobro quedó pendiente en servidor';
							nuevaCola.push(item);
							enviarUno(index + 1, 0);
							return;
						}
						marcarExitoCola(data);
						return;
					}
					return verificarSiYaEnServidor().then((ya) => {
						if (ya && posknRespuestaEsAckPedido(ya)) {
							marcarExitoCola(ya);
							return;
						}
						const msgErrCola = win.posknParseMensajeErrorAjax(response);
						if (msgErrCola && !win.posknErrorPedidoEsRecuperable(msgErrCola, response)) {
							item._error_servidor = msgErrCola;
							item.outbox_status = 'failed_business';
							nuevaCola.push(item);
							if (typeof win.mostrarNotificacion === 'function') {
								win.mostrarNotificacion(`Pedido pendiente con error: ${msgErrCola}`, 'error');
							}
							enviarUno(index + 1, 0);
							return;
						}
						if (msgErrCola) {
							item._error_servidor = msgErrCola;
						}
						item.intentos = (parseInt(item.intentos, 10) || 0) + 1;
						item.ultimoIntento = new Date().toISOString();
						if (reintentoItem < COLA_REINTENTOS_POR_ITEM) {
							const delay = Math.min(
								COLA_DELAY_REINTENTO_MS * Math.pow(2, reintentoItem),
								20000,
							);
							win.setTimeout(() => enviarUno(index, reintentoItem + 1), delay);
							return;
						}
						nuevaCola.push(item);
						if (msgErrCola && typeof win.mostrarNotificacion === 'function') {
							win.mostrarNotificacion(`No se pudo subir el pedido: ${msgErrCola}`, 'error');
						}
						enviarUno(index + 1, 0);
					});
				})
				.catch((xhr) => {
					if (estaAnuladoCola(win, clientId) || (state.colaSyncGeneration || 0) !== syncGen) {
						enviarUno(index + 1, 0);
						return;
					}
					verificarSiYaEnServidor().then((ya) => {
						if (ya && posknRespuestaEsAckPedido(ya)) {
							marcarExitoCola(ya);
							return;
						}
						const msgErr = win.posknParseMensajeErrorAjax(xhr);
						if (msgErr && !win.posknErrorPedidoEsRecuperable(msgErr, xhr)) {
							item._error_servidor = msgErr;
							item.outbox_status = 'failed_business';
							nuevaCola.push(item);
							if (typeof win.mostrarNotificacion === 'function') {
								win.mostrarNotificacion(`Pedido pendiente con error: ${msgErr}`, 'error');
							}
							enviarUno(index + 1, 0);
							return;
						}
						if (msgErr) item._error_servidor = msgErr;
						item.intentos = (parseInt(item.intentos, 10) || 0) + 1;
						item.ultimoIntento = new Date().toISOString();
						if (reintentoItem < COLA_REINTENTOS_POR_ITEM && esErrorRed(xhr, xhr && xhr.statusText)) {
							const delay = Math.min(
								COLA_DELAY_REINTENTO_MS * Math.pow(2, reintentoItem),
								20000,
							);
							win.setTimeout(() => enviarUno(index, reintentoItem + 1), delay);
							return;
						}
						nuevaCola.push(item);
						enviarUno(index + 1, 0);
					});
				});
		}

		enviarUno(0, 0);
	};

	initOfflineQueueBoot(win, $);

	win.crearPedido = () => {
		if (win.procesandoPedido) {
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('Ya se está procesando un pedido, por favor espera...', 'error');
			}
			return;
		}
		if (typeof win.posknRequireRegisterSelected === 'function' && !win.posknRequireRegisterSelected()) {
			return;
		}

		const carrito = win.carrito || [];
		if (!carrito.length) {
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('El carrito está vacío', 'error');
			}
			return;
		}

		const iniciarProcesoPedido = (modoOffline) => {
			if (modoOffline) {
				if (!$('#modal-pago').is(':visible')) {
					if (typeof win.abrirModalPago === 'function') {
						win.abrirModalPago();
					}
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion(
							'Sin conexión. Selecciona el método de pago y pulsa CREAR PEDIDO para guardarlo en cola.',
							'warning',
						);
					}
					return;
				}
				if ($('#btn-crear-pedido').prop('disabled')) {
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion('Selecciona un método de pago válido antes de crear el pedido.', 'error');
					}
					return;
				}
				if (!cajaAbiertaLocalmente($, win)) {
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion(
							'Sin conexión y caja no verificada. Abre caja antes de vender o reconecta para confirmar.',
							'error',
						);
					}
					return;
				}
				win._posknOfflineOrderMode = true;
			}
			if (typeof win.procesarPedido === 'function') {
				win.procesarPedido();
			}
		};

		if (typeof win.posknIsOfflineMode === 'function' && win.posknIsOfflineMode()) {
			iniciarProcesoPedido(true);
			return;
		}

		win.POSKN.api.getCajaStatus(8000)
			.then((response) => {
				if (response.success && response.data.estado === 'abierta') {
					win._posknCajaCache = { estado: 'abierta', ts: Date.now(), data: response.data };
					iniciarProcesoPedido(false);
				} else {
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion('❌ Debes abrir una caja antes de crear pedidos', 'error');
					}
					if (win.confirm('¿Deseas abrir una caja ahora?') && typeof win.mostrarModuloCaja === 'function') {
						win.mostrarModuloCaja();
					}
				}
			})
			.catch((xhr, status) => {
				const cache = win._posknCajaCache;
				const cacheAbierta =
					cache &&
					cache.estado === 'abierta' &&
					cache.ts &&
					Date.now() - cache.ts < 15 * 60 * 1000;
				if ((esErrorRed(xhr, status) || status === 'timeout') && carrito.length > 0) {
					if (cacheAbierta) {
						iniciarProcesoPedido(false);
						return;
					}
					iniciarProcesoPedido(true);
					return;
				}
				if (status === 'timeout') {
					if (typeof win.mostrarNotificacion === 'function') {
						win.mostrarNotificacion('Tiempo agotado al verificar caja. Intenta de nuevo.', 'error');
					}
				} else if (typeof win.mostrarNotificacion === 'function') {
					win.mostrarNotificacion('Error al verificar estado de caja', 'error');
				}
			});
	};

	win.posknEsErrorRed = esErrorRed;
	win.posknNotificarPedidoEnColaOffline = notificarPedidoEnColaOffline;
}
